import type * as EditorTypes from '@wix/editor-component-types';

import type {
  IEditorAddPanelItem,
  IEditorAddPanelModel,
} from '../editorExternal';
import type { IStylablePanelConfig } from '../stylableExternal';
import type { StylableAPIProps } from './stylable';
import type { CompRef, DocumentServicesObject } from './documentManagement';
import type { QuickEditControlEntry } from './quickEditControls';

export * from '../editorExternal/store';
export * from './stylable';
export type { EditorTypes, DocumentServicesObject };

export type Bi = {
  event: (value: any, data: any) => void;
};

type BiEventFieldTypeMap = {
  string: string;
  number: number; // TODO: is it correct type name?
  numeric: number;
  bool: boolean;
  boolean: boolean; // TODO: is it correct type name?
  guid: string;
  datetime: Date;
  url: string;
};

export type BiEventFieldType = keyof BiEventFieldTypeMap;

export type BiEventDefinition = {
  evid?: number;
  eventId?: number; // TODO: why evid vs eventId in some events?
  src?: number;
  params?: Record<string, string>; // TODO: why prams vs fields in some events?
  fields?: Record<string, BiEventFieldType>;
};

export type BiEventFields<T extends Record<string, BiEventFieldType> = any> = {
  [key in keyof T]?: BiEventFieldTypeMap[T[key]];
};

export interface BiErrorDefinition {
  errorCode: number;
  errorName: string;
  severity: 'error' | 'fatal' | 'warning' | 'recoverable';
  dsc?: string;
  params?: {
    p1?: string;
    p2?: string;
    p3?: string;
    p4?: string;
  };
}

export type BiErrorFields = Record<string, unknown>;

export type SendBIFunction = <T extends BiEventDefinition>(
  biEventDefinition: T,
  biEventFields?: any, // BiEventFields<T['fields']>,
) => void;

export type SendBIErrorFunction = (
  error: BiErrorDefinition,
  params: BiErrorFields,
) => void;

type IEditorElementsPanelProps = {
  updateStyleProperties(
    styleProperties: Record<string, string | number | boolean>,
  ): void;
};

type IEditorPanelProps<TComponentProperties, TComponentData> =
  EditorTypes.ComponentPanelProps<TComponentProperties, TComponentData> & {
    isMobileEditor: boolean;
    isDeveloperModeEnabled: boolean;
    updateDesign: (
      value: EditorTypes.ComponentPanelProps<
        TComponentProperties,
        TComponentData
      >['compLayout'],
    ) => void;
    updateStyle: (
      value: EditorTypes.ComponentPanelProps<
        TComponentProperties,
        TComponentData
      >['compStyle'],
    ) => void;
    updateLayout: (
      value: EditorTypes.ComponentPanelProps<
        TComponentProperties,
        TComponentData
      >['compLayout'],
    ) => void;
    compType: string;
    panelName: string;
    sendSelectedComponentBI: SendBIFunction;
    experimentIsOpen: (key: string) => boolean;
    setComponentPreviewState: (componentId: string, val: string | null) => void;
    selectedComponents: Array<CompRef>;
    bi: Bi;
    origin: string;
    translate(key: string, values?: Record<string, any> | string): string;
    replaceComponentPanel(
      panelName: string,
      panelProps?: Record<string, any>,
    ): void;
  };

type IComponentPanelProps<TComponentProperties, TComponentData> =
  IEditorPanelProps<TComponentProperties, TComponentData> &
    IEditorElementsPanelProps;

type IComponentPanelDefinition<
  TComponentProperties,
  TComponentData,
  TMapperProps = {},
> = Omit<
  EditorTypes.ComponentPanelDefinition<TComponentProperties, TComponentData>,
  'component' | 'PanelClass'
> & {
  component: React.ComponentType<
    IComponentPanelProps<TComponentProperties, TComponentData> & TMapperProps
  >;
};

export type {
  IComponentPanelDefinition,
  IComponentPanelProps,
  IEditorAddPanelItem,
  IEditorAddPanelModel,
  IEditorPanelProps,
  IEditorElementsPanelProps,
};

export type {
  ComponentPanelUpdateComponentData as IComponentPanelUpdateComponentData,
  ComponentPanelUpdateComponentProperties as IComponentPanelUpdateComponentProperties,
  ComponentEditorMetaDataRaw as IComponentEditorMetaDataRaw,
  ComponentGFPPDefinition as IComponentGFPPDefinition,
  GFPPDataRaw as IGFPPDataRaw,
  GFPPAction as IGFPPAction,
  /**
   * Host API
   */
  EditorHostAPI as IEditorHostAPI,
} from '@wix/editor-component-types';

// TODO: find a way to split DocumentServicesObject into two types
export type DSRead = DocumentServicesObject;
export type DSAction = DocumentServicesObject;

// TODO - Replace with CompDefinition
export type IComponentPanelWithStylable<Props, Data> = IComponentPanelProps<
  Props,
  Data
> &
  StylableAPIProps;

export type IEditorMetadata =
  Partial<EditorTypes.ComponentEditorMetaDataDefinition>;

export type IEditorTranslationFetcher = (
  locale: string,
) => Promise<Array<Record<string, string>>>;

export type ITranslationFetcher = (options: {
  locale: string;
}) => Promise<Array<Record<string, string>>>;

export type IAddPanelResolver = (options: {
  editorAPI: any;
}) => IEditorAddPanelModel;

/**
 * Component Model
 */
export interface IEditorComponentModel<
  TComponentProperties = Record<string, any>,
  TComponentData = Record<string, any>,
> {
  gfpp: EditorTypes.ComponentGFPPDefinition;
  quickEditControl?: QuickEditControlEntry;
  metadata?: IEditorMetadata;
  settingsPanel?: EditorTypes.ComponentPanelDefinition<
    TComponentProperties,
    TComponentData
  >;
  layoutPanel?: EditorTypes.ComponentPanelDefinition<
    TComponentProperties,
    TComponentData
  >;
  managePanel?: EditorTypes.ComponentPanelDefinition<
    TComponentProperties,
    TComponentData
  >;
  backgroundPanel?: EditorTypes.ComponentPanelDefinition<
    TComponentProperties,
    TComponentData
  >;
  behaviorsPanel?: EditorTypes.ComponentPanelDefinition<
    TComponentProperties,
    TComponentData
  >;
  animationPanel?: EditorTypes.ComponentPanelDefinition<
    TComponentProperties,
    TComponentData
  >;
  devPanel?: EditorTypes.ComponentPanelDefinition<
    TComponentProperties,
    TComponentData
  >;
  stylablePanel?: IStylablePanelConfig;
  addPanel?: IEditorAddPanelModel | IAddPanelResolver;
  translations?: IEditorTranslationFetcher;
}

export enum EditorPanelWidth {
  Regular = 'Regular',
  Wide = 'Wide',
}
